import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaHeart,
  FaBell,
  FaMapMarkerAlt,
  FaWallet,
  FaGift,
  FaHeadset,
  FaLock,
  FaSpinner,
  FaTrash,
  FaSearch,
  FaCopy,
  FaWhatsapp,
  FaInfoCircle,
  FaTimes,
  FaPlus,
  FaEdit,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { useVehicle } from '../../contexts/VehicleContext';
import { accountSettingsService } from '../../services/apiService';

const TABS = [
  { id: 'wishlist', label: 'Wishlist', icon: FaHeart },
  { id: 'notifications', label: 'Notifications', icon: FaBell },
  { id: 'addresses', label: 'Addresses', icon: FaMapMarkerAlt },
  { id: 'wallet', label: 'Wallet', icon: FaWallet },
  { id: 'referral', label: 'Referral', icon: FaGift },
  { id: 'support', label: 'Support', icon: FaHeadset },
  { id: 'privacy', label: 'Privacy', icon: FaLock },
];

const NOTIFICATION_CATEGORIES = [
  { key: 'orderUpdates', label: 'Order updates', description: 'Status changes, delivery updates' },
  { key: 'warrantyAlerts', label: 'Warranty alerts', description: 'Expiry and claim notifications' },
  { key: 'serviceReminders', label: 'Service reminders', description: 'Upcoming or overdue service' },
  { key: 'promotionalNudges', label: 'Promotional nudges', description: 'Deals and offers' },
];

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <label className="relative inline-flex cursor-pointer items-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="peer sr-only"
        aria-label={label}
      />
      <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-disabled:opacity-50" />
    </label>
  );
}

function StockChip({ inStock }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
        inStock ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {inStock ? 'In stock' : 'Out of stock'}
    </span>
  );
}

function AddressForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(
    initial || {
      label: 'Home',
      name: '',
      mobile: '',
      fullAddress: '',
      city: '',
      state: '',
      pincode: '',
      isDefault: false,
    }
  );

  return (
    <form
      className="mt-3 space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          value={form.label}
          onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          placeholder="Label (Home, Work…)"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Full name"
          required
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          value={form.mobile}
          onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
          placeholder="Mobile"
          required
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          value={form.pincode}
          onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))}
          placeholder="Pincode"
          required
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </div>
      <input
        value={form.fullAddress}
        onChange={(e) => setForm((f) => ({ ...f, fullAddress: e.target.value }))}
        placeholder="Full address"
        required
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          value={form.city}
          onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
          placeholder="City"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          value={form.state}
          onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
          placeholder="State"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={form.isDefault}
          onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
        />
        Set as default
      </label>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save address'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

/**
 * B-10E — Wishlist, notifications & account settings
 */
const AccountSettings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout } = useAuth();
  const { selectedVehicle } = useVehicle();

  const tab = searchParams.get('tab') || 'wishlist';
  const setTab = (id) => setSearchParams({ tab: id });

  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [prefsSaving, setPrefsSaving] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [referral, setReferral] = useState(null);
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressSaving, setAddressSaving] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [privacyBusy, setPrivacyBusy] = useState('');

  const loadWishlist = useCallback(async () => {
    const items = await accountSettingsService.getWishlist();
    setWishlist(items);
  }, []);

  const loadTabData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'wishlist') await loadWishlist();
      else if (tab === 'notifications') {
        const p = await accountSettingsService.getNotificationPreferences();
        setPrefs(p);
      } else if (tab === 'addresses') {
        setAddresses(await accountSettingsService.getAddresses());
      } else if (tab === 'wallet') {
        setWallet(await accountSettingsService.getWallet());
      } else if (tab === 'referral') {
        setReferral(await accountSettingsService.getReferral());
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [tab, loadWishlist]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  useEffect(() => {
    if (tab !== 'wishlist') return undefined;
    const timer = setInterval(() => {
      loadWishlist().catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [tab, loadWishlist]);

  const handleRemoveWishlist = async (id) => {
    try {
      await accountSettingsService.removeFromWishlist(id);
      setWishlist((items) => items.filter((w) => w.id !== id));
      toast.success('Removed from wishlist');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const findParts = (item) => {
    const vehicleId =
      item.vehicleId || selectedVehicle?._id || selectedVehicle?.id || '';
    if (item.nodeId) {
      const params = new URLSearchParams({
        column: item.catalogColumn || 'oem',
        nodeId: item.nodeId,
      });
      if (vehicleId) params.set('vehicleId', vehicleId);
      navigate(`/catalog?${params.toString()}`);
      return;
    }
    const q = item.partNumber || item.partName;
    const params = new URLSearchParams({ q });
    if (vehicleId) params.set('vehicleId', vehicleId);
    if (selectedVehicle?.make) params.set('vehicleMake', selectedVehicle.make);
    if (selectedVehicle?.model) params.set('vehicleModel', selectedVehicle.model);
    navigate(`/catalog?${params.toString()}`);
  };

  const patchPref = async (category, channel, value) => {
    setPrefsSaving(`${category}.${channel}`);
    let previous = null;
    setPrefs((current) => {
      previous = current;
      return {
        ...current,
        [category]: { ...current[category], [channel]: value },
      };
    });
    try {
      const updated = await accountSettingsService.updateNotificationPreferences({
        [category]: { [channel]: value },
      });
      setPrefs(updated);
    } catch (err) {
      if (previous) setPrefs(previous);
      toast.error(err.message);
    } finally {
      setPrefsSaving('');
    }
  };

  const saveAddress = async (form) => {
    setAddressSaving(true);
    try {
      if (editingAddress) {
        await accountSettingsService.updateAddress(editingAddress._id || editingAddress.id, form);
        toast.success('Address updated');
      } else {
        await accountSettingsService.addAddress(form);
        toast.success('Address added');
      }
      setAddressFormOpen(false);
      setEditingAddress(null);
      setAddresses(await accountSettingsService.getAddresses());
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddressSaving(false);
    }
  };

  const openChat = () => {
    window.dispatchEvent(new CustomEvent('open-chatbot'));
    toast.info('Opening support chat…');
  };

  const shareWhatsApp = (url) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      const result = await accountSettingsService.deleteAccount();
      toast.success(result.message || 'Account scheduled for deletion');
      setDeleteStep(0);
      logout?.();
      navigate('/');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-3xl">
        <Link to="/account" className="text-sm font-medium text-blue-600 hover:text-blue-700">
          ← Back to account
        </Link>
        <h1 className="mt-3 text-2xl font-bold text-gray-900">Account settings</h1>
        <p className="mt-1 text-sm text-gray-500">Wishlist, alerts, addresses, wallet & privacy</p>

        <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold ${
                  active
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="text-xs" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
          {loading ? (
            <div className="flex justify-center py-12 text-gray-400">
              <FaSpinner className="animate-spin text-2xl" />
            </div>
          ) : tab === 'wishlist' ? (
            <>
              <h2 className="text-lg font-bold text-gray-900">Saved parts</h2>
              <p className="mt-1 text-sm text-gray-500">Notify-when-available alerts from part pages</p>
              {wishlist.length === 0 ? (
                <div className="mt-8 text-center">
                  <FaHeart className="mx-auto text-3xl text-gray-300" />
                  <p className="mt-3 font-medium text-gray-700">No saved parts yet</p>
                  <Link to="/catalog" className="mt-4 inline-block text-sm font-semibold text-blue-600">
                    Browse catalogue →
                  </Link>
                </div>
              ) : (
                <ul className="mt-4 space-y-3">
                  {wishlist.map((item) => (
                    <li
                      key={item.id}
                      className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 break-words">{item.partName}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {item.currentPrice != null && (
                            <span className="text-sm font-bold text-gray-800">
                              ₹{Number(item.currentPrice).toLocaleString('en-IN')}
                            </span>
                          )}
                          <StockChip inStock={item.inStock} />
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() => findParts(item)}
                          className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                        >
                          Find parts
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveWishlist(item.id)}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : tab === 'notifications' && prefs ? (
            <>
              <h2 className="text-lg font-bold text-gray-900">Notification preferences</h2>
              <p className="mt-1 text-sm text-gray-500">Changes save immediately</p>
              <div className="mt-4 space-y-4">
                {NOTIFICATION_CATEGORIES.map((cat) => (
                  <div key={cat.key} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                    <p className="font-semibold text-gray-900">{cat.label}</p>
                    <p className="text-xs text-gray-500">{cat.description}</p>
                    <div className="mt-3 flex flex-wrap gap-6">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">WhatsApp</span>
                        <Toggle
                          label={`${cat.label} WhatsApp`}
                          checked={!!prefs[cat.key]?.whatsapp}
                          disabled={prefsSaving === `${cat.key}.whatsapp`}
                          onChange={(v) => patchPref(cat.key, 'whatsapp', v)}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">In-app push</span>
                        <Toggle
                          label={`${cat.label} push`}
                          checked={!!prefs[cat.key]?.push}
                          disabled={prefsSaving === `${cat.key}.push`}
                          onChange={(v) => patchPref(cat.key, 'push', v)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : tab === 'addresses' ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-gray-900">Saved addresses</h2>
                <button
                  type="button"
                  onClick={() => {
                    setEditingAddress(null);
                    setAddressFormOpen(true);
                  }}
                  className="inline-flex items-center gap-1 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white"
                >
                  <FaPlus /> Add
                </button>
              </div>
              {addressFormOpen && (
                <AddressForm
                  key={editingAddress?._id || editingAddress?.id || 'new'}
                  initial={
                    editingAddress
                      ? {
                          label: editingAddress.label,
                          name: editingAddress.name,
                          mobile: editingAddress.mobile,
                          fullAddress: editingAddress.fullAddress,
                          city: editingAddress.city,
                          state: editingAddress.state,
                          pincode: editingAddress.pincode,
                          isDefault: editingAddress.isDefault,
                        }
                      : undefined
                  }
                  onSave={saveAddress}
                  onCancel={() => {
                    setAddressFormOpen(false);
                    setEditingAddress(null);
                  }}
                  saving={addressSaving}
                />
              )}
              {addresses.length === 0 && !addressFormOpen ? (
                <p className="mt-6 text-center text-sm text-gray-500">No saved addresses</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {addresses.map((addr) => (
                    <li
                      key={addr._id || addr.id}
                      className={`rounded-xl border p-4 ${addr.isDefault ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {addr.label}
                            {addr.isDefault && (
                              <span className="ml-2 rounded bg-blue-600 px-1.5 py-0.5 text-[10px] text-white">
                                Default
                              </span>
                            )}
                          </p>
                          <p className="mt-1 text-sm text-gray-600">{addr.name} · {addr.mobile}</p>
                          <p className="text-sm text-gray-600">{addr.fullAddress}</p>
                          <p className="text-sm text-gray-500">
                            {[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          {!addr.isDefault && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await accountSettingsService.setDefaultAddress(addr._id || addr.id);
                                  setAddresses(await accountSettingsService.getAddresses());
                                  toast.success('Default address updated');
                                } catch (err) {
                                  toast.error(err.message);
                                }
                              }}
                              className="rounded p-2 text-xs text-blue-600 hover:bg-blue-100"
                            >
                              Default
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingAddress(addr);
                              setAddressFormOpen(true);
                            }}
                            className="rounded p-2 text-gray-500 hover:bg-gray-100"
                            aria-label="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await accountSettingsService.deleteAddress(addr._id || addr.id);
                                setAddresses(await accountSettingsService.getAddresses());
                                toast.success('Address deleted');
                              } catch (err) {
                                toast.error(err.message);
                              }
                            }}
                            className="rounded p-2 text-red-500 hover:bg-red-50"
                            aria-label="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : tab === 'wallet' && wallet ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Wallet</h2>
                  <p className="mt-1 text-3xl font-bold text-gray-900">
                    ₹{Number(wallet.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <span
                  className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-1 text-xs text-gray-600"
                  title="Use wallet balance at checkout to pay for parts and services. Refunds are credited here."
                >
                  <FaInfoCircle /> How to use
                </span>
              </div>
              <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-gray-500">Transactions</h3>
              {(wallet.transactions || []).length === 0 ? (
                <p className="mt-3 text-sm text-gray-500">No transactions yet</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {wallet.transactions.map((tx, i) => (
                    <li key={tx.id || i} className="flex justify-between rounded-lg border border-gray-100 px-3 py-2 text-sm">
                      <span>{tx.description || tx.type}</span>
                      <span className="font-semibold">
                        {tx.type === 'credit' ? '+' : '-'}₹{Number(tx.amount || 0).toLocaleString('en-IN')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : tab === 'referral' && referral ? (
            <>
              <h2 className="text-lg font-bold text-gray-900">Refer a friend</h2>
              <p className="mt-1 text-sm text-gray-600">{referral.creditNote}</p>
              <p className="mt-4 break-all rounded-lg bg-gray-50 px-3 py-2 font-mono text-xs text-gray-800">
                {referral.shareUrl}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(referral.shareUrl);
                    toast.success('Referral link copied');
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
                >
                  <FaCopy /> Copy
                </button>
                <button
                  type="button"
                  onClick={() => shareWhatsApp(referral.shareUrl)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  <FaWhatsapp /> Share on WhatsApp
                </button>
              </div>
            </>
          ) : tab === 'support' ? (
            <>
              <h2 className="text-lg font-bold text-gray-900">Customer support</h2>
              <p className="mt-1 text-sm text-gray-500">Average response time: ~5 minutes</p>
              <button
                type="button"
                onClick={openChat}
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <FaHeadset /> Chat with us
              </button>
            </>
          ) : tab === 'privacy' ? (
            <>
              <h2 className="text-lg font-bold text-gray-900">Privacy & data</h2>
              <div className="mt-4 space-y-3">
                <button
                  type="button"
                  disabled={privacyBusy === 'search'}
                  onClick={async () => {
                    setPrivacyBusy('search');
                    try {
                      await accountSettingsService.clearSearchHistory();
                      toast.success('Search history cleared');
                    } catch (err) {
                      toast.error(err.message);
                    } finally {
                      setPrivacyBusy('');
                    }
                  }}
                  className="flex w-full items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50"
                >
                  <FaSearch className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">Clear search history</span>
                </button>
                <button
                  type="button"
                  disabled={privacyBusy === 'export'}
                  onClick={async () => {
                    setPrivacyBusy('export');
                    try {
                      const result = await accountSettingsService.requestDataExport();
                      toast.success(result.message || 'Export requested');
                    } catch (err) {
                      toast.error(err.message);
                    } finally {
                      setPrivacyBusy('');
                    }
                  }}
                  className="flex w-full items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50"
                >
                  <FaLock className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">Download my data (request)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteStep(1)}
                  className="flex w-full items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-left hover:bg-red-100"
                >
                  <FaTrash className="text-red-500" />
                  <span className="text-sm font-medium text-red-800">Delete account</span>
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {deleteStep > 0 && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {deleteStep === 1 ? 'Delete account?' : 'Confirm deletion'}
              </h3>
              <button type="button" onClick={() => { setDeleteStep(0); setDeleteConfirmText(''); }} aria-label="Close">
                <FaTimes className="text-gray-400" />
              </button>
            </div>
            {deleteStep === 1 ? (
              <>
                <p className="mt-3 text-sm text-gray-600">
                  Your account will be soft-deleted with a <strong>30-day recovery window</strong>. After that,
                  your data is permanently removed.
                </p>
                <div className="mt-6 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteStep(0)}
                    className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteStep(2)}
                    className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-semibold text-white"
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-3 text-sm text-gray-600">
                  Type <strong>DELETE</strong> to confirm. You can contact support within 30 days to recover
                  your account.
                </p>
                <input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  disabled={deleting}
                  onClick={handleDeleteAccount}
                  className="mt-4 w-full rounded-lg bg-red-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Delete my account'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
